import { inject, Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, shareReplay, switchMap, take, tap } from 'rxjs/operators';

import { CustomerService } from '@services/customer.service';
import { BaseDropdownInterface } from '@interfaces/base-dropdown.interface';

@Injectable()
export class CustomerOnboardingContextService {
  private readonly customerService = inject(CustomerService);

  private readonly customerIdSubject: ReplaySubject<string> = new ReplaySubject<string>(1);

  private currentCustomerId = '';

  private currentCustomerName = '';

  private readonly customerId$: Observable<string> = this.customerIdSubject.asObservable().pipe(distinctUntilChanged());

  private readonly customer$: Observable<BaseDropdownInterface> = this.customerId$.pipe(
    switchMap((customerId: string): Observable<BaseDropdownInterface> => this.customerService.getCustomerName(customerId)),
    tap((customer: BaseDropdownInterface): void => {
      this.currentCustomerName = customer?.name ?? '';
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  /**
   * Sets current customer identifier for onboarding context stream.
   * @param customerId - customer identifier from route params
   */
  initialize(customerId: string): void {
    this.currentCustomerId = customerId;

    this.customerIdSubject.next(customerId);
  }

  /**
   * Loads onboarding context once and completes.
   * Used by route resolvers to ensure customer context is available before activation.
   * @param customerId - customer identifier from route params
   */
  ensureLoaded$(customerId: string): Observable<BaseDropdownInterface> {
    this.initialize(customerId);

    return this.customer$.pipe(take(1));
  }

  /**
   * Current onboarding customer identifier.
   */
  get customerId(): string {
    return this.currentCustomerId;
  }

  /**
   * Current onboarding customer name.
   */
  get customerName(): string {
    return this.currentCustomerName;
  }
}
